import { useCallback, useEffect, useMemo, useState } from "react";

import { FormProvider } from "react-hook-form";
import { useLocation } from "react-router-dom";

import CloseIcon from "@mui/icons-material/Close";

import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Button, Dialog, Grid, Paper, Typography } from "@mui/material";

import { useAuthContext } from "@covertree/auth";
import { useFormWithNotification } from "@covertree/hooks";
import { useCreateOrUpdateHubspotContactMutation } from "@covertree/new-business-graphql";
import {
  selectGeoCodeResult,
  useNewBusinessSelector,
} from "@covertree/new-business-redux";
import {
  Form,
  FormEmailAutocorrect,
  FormTextField,
  SubmitButton,
} from "@covertree/ui";
import { IconButton } from "@covertree/ui/buttons/IconButton";
import { Toast } from "@covertree/utils";

import { GENERAL_EMAIL_ADDRESS } from "@consts/index";
import { getFullStateName } from "@utils/getFullStateName";

import { NotInStateModalForm, notInStateModalFormValidator } from "./form";

interface NonInStateModalProps {
  open: boolean;
  onClose: () => void;
}

export function NonInStateModal({ open, onClose }: NonInStateModalProps) {
  const newHomeGeoCode = useNewBusinessSelector(selectGeoCodeResult);

  const [isConfirmationModalOpened, setIsConfirmationModalOpened] =
    useState<boolean>(false);
  const [hubspotMessage, setHubspotMessage] = useState<string>("");

  const fullStateName = useMemo(
    () => (newHomeGeoCode?.state ? getFullStateName(newHomeGeoCode.state) : ""),
    [newHomeGeoCode]
  );

  const { pathname } = useLocation();

  const [createOrUpdateHubspotContact, { loading }] =
    useCreateOrUpdateHubspotContactMutation();

  const form = useFormWithNotification({
    mode: "all",
    reValidateMode: "onSubmit",
    resolver: yupResolver(notInStateModalFormValidator),
  });

  const { control, handleSubmit, setValue } = form;

  useEffect(() => {
    setValue("usa_state", fullStateName);
  }, [fullStateName]);

  const { hasGroup } = useAuthContext();
  const leadSource =
    hasGroup("ct_master_agent") || hasGroup("ct_agent")
      ? "Internal"
      : "External";

  const onSubmit = useCallback(
    async (hubspotFormValues: NotInStateModalForm) => {
      try {
        const result = await createOrUpdateHubspotContact({
          variables: {
            input: {
              properties: {
                ...hubspotFormValues,
                lead_source: [leadSource],
              },
              context: {
                pageName: `Agent - ${pathname.split("/").pop()!}`,
                pageUri: window.location.href,
              },
            },
          },
        });

        if (result.errors) {
          setHubspotMessage("Could not save lead to HubSpot");
          setIsConfirmationModalOpened(true);
          return;
        }
        setHubspotMessage("Lead submitted to HubSpot successfully!");
        setIsConfirmationModalOpened(true);
      } catch {
        Toast.showError("Could not connect to HubSpot");
      }
    },
    [createOrUpdateHubspotContact, leadSource, pathname]
  );

  const handleCloseConfirmationModal = () => {
    setIsConfirmationModalOpened(false);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose}>
        <FormProvider {...form}>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Paper
              sx={(theme) => ({
                backgroundColor: theme.palette.background.paper,
                border: "none",
                boxShadow: theme.shadows[5],
                padding: theme.spacing(2, 4, 3),
              })}
              variant="outlined">
              <Grid container spacing={2}>
                <Grid item xs={11}>
                  <Typography>
                    CoverTree isn&rsquo;t in{" "}
                    <Typography
                      component="span"
                      sx={(theme) => ({ color: theme.colors.green3 })}>
                      {fullStateName}
                    </Typography>{" "}
                    yet. Fill out the form to store this client&rsquo;s
                    information for a future lead.
                  </Typography>
                </Grid>

                <Grid
                  item
                  xs={1}
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                  }}>
                  <IconButton
                    data-testid="drawer-close-button"
                    sx={(theme) => ({
                      width: theme.spacing(5),
                      height: theme.spacing(5),
                      color: theme.colors.green3,
                      background: theme.colors.white,
                      border: `2px solid ${theme.colors.green3}`,
                      "&:hover": {
                        color: theme.colors.white,
                        backgroundColor: theme.colors.green3,
                      },
                    })}
                    onClick={onClose}>
                    <CloseIcon />
                  </IconButton>
                </Grid>

                <Grid item xs={6}>
                  <FormTextField
                    id="potential-first-name"
                    name="firstname"
                    label="First Name"
                    placeholder="Jake"
                    control={control}
                  />
                </Grid>

                <Grid item xs={6}>
                  <FormTextField
                    id="potential-last-name"
                    name="lastname"
                    label="Last Name"
                    placeholder="Peralta"
                    control={control}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormEmailAutocorrect
                    id="potential-email"
                    name="email"
                    label="Email"
                    placeholder={GENERAL_EMAIL_ADDRESS}
                    control={control}
                    setValue={setValue}
                  />
                </Grid>

                <Grid item xs={6}>
                  <FormTextField
                    id="potential-phone"
                    name="phone"
                    label="Phone"
                    placeholder={"800-456-7890"}
                    control={control}
                  />
                </Grid>

                <Grid item xs={6}>
                  <FormTextField
                    id="potential-state"
                    name="usa_state"
                    label="State"
                    InputProps={{ readOnly: true }}
                    control={control}
                  />
                </Grid>

                <Grid
                  item
                  xs={12}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                  }}>
                  <SubmitButton loading={loading}>Submit</SubmitButton>
                </Grid>
              </Grid>
            </Paper>
          </Form>
        </FormProvider>
      </Dialog>
      <Dialog
        open={isConfirmationModalOpened}
        onClose={handleCloseConfirmationModal}>
        <Paper
          sx={{
            marginX: 5,
            marginY: 3,
            textAlign: "center",
            boxShadow: "none",
          }}>
          <Box
            sx={{
              marginX: 3,
              marginY: 2,
            }}>
            <Typography>{hubspotMessage}</Typography>
            <Button onClick={handleCloseConfirmationModal}>Done</Button>
          </Box>
        </Paper>
      </Dialog>
    </>
  );
}
